import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import isArchived from 'archive/isArchived';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {
  getRestrictionAccessDisplayText,
  getCICOStatusText,
  getCICOWarningText,
  getSampleStatusTooltip,
  doesCurrentUserHaveAccessForItem,
  isItemAvailable,
  isItemAvailableToCurrentUser,
  doesItemHaveBlockingStatusWarning,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface StatusLabelComponentProps {
  className?: string;
  isWarning: boolean;
  isBlocking: boolean;
  showCheckmark?: boolean;
  message: string | null;
}

export function shouldShowWarning(
  item: SampleStatusItem,
  includeAuthorization: boolean,
  includeAvailability: boolean,
  includeExpiration: boolean = false
): boolean {
  const showUnauthorizedWarning = includeAuthorization && !doesCurrentUserHaveAccessForItem(item);
  const showUnavailableWarning = includeAvailability && !isItemAvailableToCurrentUser(item);
  const showExpiredWarning = includeExpiration && item.expirationInfo?.isExpired;
  return showUnauthorizedWarning || showUnavailableWarning || Boolean(showExpiredWarning);
}

export function StatusLabelComponent(props: StatusLabelComponentProps): JSX.Element {
  const getIcon = (): JSX.Element | null => {
    if (props.isBlocking) {
      return (
        <BenchlingIcon
          className={classNames(css(styles.colorRedDark), {'u-mini-spacing-right': props.message != null})}
          icon="revision"
        />
      );
    }
    if (props.isWarning) {
      return (
        <BenchlingIcon
          className={classNames(css(styles.colorOrange), {'u-mini-spacing-right': props.message != null})}
          icon="warning"
        />
      );
    }
    if (props.showCheckmark) {
      return (
        <BenchlingIcon
          className={classNames(css(styles.colorGreen), {'u-mini-spacing-right': props.message != null})}
          icon="done"
        />
      );
    }
    return null;
  };

  return (
    <span className={props.className} data-test-component="SampleStatusLabel" data-test-element="root">
      {getIcon()}
      {props.message}
    </span>
  );
}

export type RenderAllStatusesProps =
  | {
      // true if "Available" and "Authorized" labels should be rendered
      renderAllStatuses: true;
      // If renderAllStatuses is true, we need to know whether the item supports sample status (CICO), so we
      // we show 'N/A' instead of 'Available'. Currently, this should only be true for containers that are
      // not well plate wells
      itemSupportsSampleStatus: boolean;
    }
  | {
      renderAllStatuses: false;
    };

interface BaseProps {
  className?: string;
  item: SampleStatusItem | null;
  includeAuthorization: boolean;
  includeAvailability: boolean;
  includeExpiration?: boolean;
  // Hide assignee in the label and instead include in the tooltip.
  hideAssigneeInLabel: boolean;
  // When this is false, will show warning icon with tooltip only. Tooltip will include assignee, if applicable
  includeMessage: boolean;
}

export type SampleStatusLabelProps = BaseProps & RenderAllStatusesProps;

export default function SampleStatusLabel(props: SampleStatusLabelProps): JSX.Element | null {
  if (props.item == null || (isArchived({...props.item, name: ''}) && !props.renderAllStatuses)) {
    return null;
  } else if (isArchived({...props.item, name: ''})) {
    return (
      <StatusLabelComponent
        className={props.className}
        isWarning={false}
        isBlocking={false}
        showCheckmark={false}
        message="N/A"
      />
    );
  }

  const getStatusText = (): string => {
    const showAuthStatus =
      props.includeAuthorization &&
      (props.renderAllStatuses || !doesCurrentUserHaveAccessForItem(props.item));
    const showAvailabilityStatus =
      props.includeAvailability && (props.renderAllStatuses || !isItemAvailable(props.item));
    const showExpiration =
      props.includeExpiration && (props.renderAllStatuses || props.item?.expirationInfo?.isExpired);
    const authorizationStatus = showAuthStatus ? getRestrictionAccessDisplayText(props.item!) : undefined;
    const expirationStatus = showExpiration
      ? props.item?.expirationInfo?.isExpired
        ? 'Expired'
        : 'Not expired'
      : undefined;
    let availabilityStatus;
    if (!showAvailabilityStatus) {
      availabilityStatus = undefined;
    } else if (!props.renderAllStatuses) {
      // The item must be unavailable
      availabilityStatus = getCICOWarningText(props.item!.checkOutStatusInfo!, {
        hideAssignee: props.hideAssigneeInLabel,
      });
    } else {
      availabilityStatus = getCICOStatusText(props.item!, props.itemSupportsSampleStatus, {
        hideAssignee: props.hideAssigneeInLabel,
      });
    }

    return [authorizationStatus, expirationStatus, availabilityStatus]
      .filter((status) => status != null)
      .join(', ');
  };

  const tooltipText = getSampleStatusTooltip(props.item, {
    includeAvailability: props.includeAvailability,
    includeAuthorization: props.includeAuthorization,
    hideAssignee: props.includeMessage && !props.hideAssigneeInLabel,
    includeExpiration: props.includeExpiration,
  });
  return (
    <Tooltip text={tooltipText} isDisabled={!tooltipText}>
      <StatusLabelComponent
        className={props.className}
        isWarning={shouldShowWarning(
          props.item,
          props.includeAuthorization,
          props.includeAvailability,
          props.includeExpiration
        )}
        isBlocking={props.includeAuthorization && doesItemHaveBlockingStatusWarning(props.item)}
        showCheckmark={props.item.checkOutStatusInfo?.isAssignedToCurrentUser && props.includeAvailability}
        message={props.includeMessage ? getStatusText() : null}
      />
    </Tooltip>
  );
}

SampleStatusLabel.defaultProps = {
  includeMessage: true,
  hideAssigneeInLabel: false,
};

export const styles = StyleSheet.create({
  colorOrange: {
    color: Colors.ORANGE,
  },
  colorGrayDark: {
    color: Colors.GRAY_DARK,
  },
  colorGreen: {
    color: Colors.GREEN,
  },
  colorRedDark: {
    color: Colors.RED_DARK,
  },
});
